const TAB_CHANNEL = "flownect-webrtc-tabs";
const TAB_ID_STORAGE_KEY = "flownect-webrtc-tab-id";
const CALL_ORIGIN_STORAGE_PREFIX = "flownect-webrtc-call-origin:";
const LIVEKIT_RELEASE_WAIT_MS = 200;

type WebRTCTabMessage =
  | { type: "tab-open"; tabId: string }
  | { type: "tab-close"; tabId: string }
  | { type: "request-livekit-release"; callUid: string; requesterTabId: string }
  | { type: "livekit-released"; callUid: string; tabId: string }
  | { type: "livekit-claimed"; callUid: string; tabId: string }
  | { type: "call-accepted"; callUid: string; tabId: string };

type CallAcceptedListener = (callUid: string) => void;

const createTabId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getWebRTCTabId = (): string => {
  if (typeof window === "undefined") return "server";

  const existing = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
  if (existing) return existing;

  const tabId = createTabId();
  sessionStorage.setItem(TAB_ID_STORAGE_KEY, tabId);
  return tabId;
};

export const markCallOriginTab = (callUid: string): void => {
  if (typeof window === "undefined") return;
  // localStorage is shared across tabs; sessionStorage is per-tab only.
  localStorage.setItem(`${CALL_ORIGIN_STORAGE_PREFIX}${callUid}`, getWebRTCTabId());
};

/** True only when this tab explicitly claimed the call (accept/initiate). */
export const isCallOwner = (callUid: string): boolean => {
  if (typeof window === "undefined") return false;
  const ownerTabId = localStorage.getItem(`${CALL_ORIGIN_STORAGE_PREFIX}${callUid}`);
  if (!ownerTabId) return false;
  return ownerTabId === getWebRTCTabId();
};

/** @deprecated Use isCallOwner */
export const isCallOriginTab = isCallOwner;

export const clearCallOriginTab = (callUid: string): void => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(`${CALL_ORIGIN_STORAGE_PREFIX}${callUid}`);
};

type LiveKitReleaseListener = (callUid: string) => void;

class WebRTCTabSession {
  private readonly tabId = getWebRTCTabId();
  private readonly openTabs = new Set<string>([this.tabId]);
  private readonly liveKitOwners = new Map<string, string>();
  private readonly supersededCalls = new Set<string>();
  private readonly releaseListeners = new Set<LiveKitReleaseListener>();
  private readonly callAcceptedListeners = new Set<CallAcceptedListener>();
  private channel: BroadcastChannel | null = null;
  private initialized = false;

  initialize(): void {
    if (this.initialized || typeof window === "undefined") return;
    this.initialized = true;

    if (typeof BroadcastChannel === "undefined") return;

    this.channel = new BroadcastChannel(TAB_CHANNEL);
    this.channel.onmessage = (event: MessageEvent<WebRTCTabMessage>) => {
      this.handleMessage(event.data);
    };

    this.postMessage({ type: "tab-open", tabId: this.tabId });
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  dispose(): void {
    if (!this.initialized || typeof window === "undefined") return;

    this.notifyTabClosing();
    window.removeEventListener("beforeunload", this.handleBeforeUnload);

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    this.initialized = false;
  }

  onLiveKitReleaseRequested(listener: LiveKitReleaseListener): () => void {
    this.releaseListeners.add(listener);
    return () => {
      this.releaseListeners.delete(listener);
    };
  }

  onCallAcceptedElsewhere(listener: CallAcceptedListener): () => void {
    this.callAcceptedListeners.add(listener);
    return () => {
      this.callAcceptedListeners.delete(listener);
    };
  }

  broadcastCallAccepted(callUid: string): void {
    this.postMessage({ type: "call-accepted", callUid, tabId: this.tabId });
  }

  /** True when no other tab is known besides this one. Call before notifyTabClosing. */
  isOnlyOpenTab(): boolean {
    return this.openTabs.size <= 1;
  }

  shouldPreferLiveKitConnect(callUid: string): boolean {
    if (typeof document === "undefined") return true;

    // Visible tabs own LiveKit audio; hidden tabs only connect when they originated the call.
    if (!document.hidden) return true;

    return isCallOwner(callUid);
  }

  wasSuperseded(callUid: string | null | undefined): boolean {
    if (!callUid) return false;
    return this.supersededCalls.has(callUid);
  }

  async prepareLiveKitConnect(callUid: string): Promise<void> {
    this.supersededCalls.delete(callUid);
    this.postMessage({
      type: "request-livekit-release",
      callUid,
      requesterTabId: this.tabId,
    });

    await this.waitForLiveKitReleases(callUid);
    this.liveKitOwners.set(callUid, this.tabId);
    this.postMessage({ type: "livekit-claimed", callUid, tabId: this.tabId });
  }

  releaseLiveKit(callUid: string, options: { superseded?: boolean } = {}): void {
    if (options.superseded) {
      this.supersededCalls.add(callUid);
    }

    if (this.liveKitOwners.get(callUid) === this.tabId) {
      this.liveKitOwners.delete(callUid);
    }

    this.postMessage({ type: "livekit-released", callUid, tabId: this.tabId });
  }

  notifyTabClosing(): void {
    this.postMessage({ type: "tab-close", tabId: this.tabId });
    this.openTabs.delete(this.tabId);
  }

  private handleBeforeUnload = (): void => {
    this.notifyTabClosing();
  };

  private handleMessage(message: WebRTCTabMessage): void {
    switch (message.type) {
      case "tab-open":
        this.openTabs.add(message.tabId);
        if (message.tabId !== this.tabId) {
          this.postMessage({ type: "tab-open", tabId: this.tabId });
        }
        break;

      case "tab-close":
        this.openTabs.delete(message.tabId);
        Array.from(this.liveKitOwners.entries()).forEach(([callUid, ownerTabId]) => {
          if (ownerTabId === message.tabId) {
            this.liveKitOwners.delete(callUid);
          }
        });
        break;

      case "request-livekit-release":
        if (message.requesterTabId === this.tabId) return;
        this.releaseListeners.forEach((listener) => {
          listener(message.callUid);
        });
        this.releaseLiveKit(message.callUid, { superseded: true });
        break;

      case "livekit-released":
        if (message.tabId === this.tabId) return;
        break;

      case "livekit-claimed":
        if (message.tabId === this.tabId) return;
        this.liveKitOwners.set(message.callUid, message.tabId);
        this.supersededCalls.add(message.callUid);
        break;

      case "call-accepted":
        if (message.tabId === this.tabId) return;
        this.supersededCalls.add(message.callUid);
        this.callAcceptedListeners.forEach((listener) => {
          listener(message.callUid);
        });
        break;

      default:
        break;
    }
  }

  private async waitForLiveKitReleases(callUid: string): Promise<void> {
    if (!this.channel) {
      await new Promise((resolve) => {
        window.setTimeout(resolve, LIVEKIT_RELEASE_WAIT_MS);
      });
      return;
    }

    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, LIVEKIT_RELEASE_WAIT_MS);
    });
  }

  private postMessage(message: WebRTCTabMessage): void {
    this.channel?.postMessage(message);
  }
}

export const webrtcTabSession = new WebRTCTabSession();
